import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { apiRequest } from '../../config/api';
import { toast } from '../ui/use-toast';
import { BarChart3, RefreshCw, DollarSign, TrendingUp, AlertCircle, Users, ChevronDown, ChevronRight, Printer } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/PhpAuthContext';

const RevenueReports = ({ viewAsAdmin = false }) => {
  const { doctors } = useData();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [overall, setOverall] = useState({
    total_revenue: 0,
    total_paid: 0,
    total_pending: 0
  });

  // Filter states
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDoctor, setSelectedDoctor] = useState('all');
  const [reportType, setReportType] = useState('monthly'); // 'monthly' or 'yearly'
  const [expandedPatients, setExpandedPatients] = useState({});
  const reportContentRef = useRef(null);

  // Generate month options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Generate year options (last 5 years to current year)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  // Format currency (INR)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDisplayDate = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const togglePatientTransactions = (doctorId, patientId) => {
    const key = `${doctorId}-${patientId}`;
    setExpandedPatients((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isSelfRevenueView = !viewAsAdmin && ['doctor', 'chief_doctor'].includes(profile?.role);
  const selectedDoctorLabel = useMemo(() => {
    if (isSelfRevenueView) return profile?.full_name || profile?.name || 'My Revenue';
    if (selectedDoctor === 'all') return 'All Doctors';
    const doctor = availableDoctors?.find((item) => String(item.id) === String(selectedDoctor));
    return doctor?.full_name || doctor?.name || doctor?.username || 'Selected Doctor';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelfRevenueView, profile?.full_name, profile?.name, selectedDoctor, doctors]);

  const exportTitle = useMemo(() => {
    const period = reportType === 'monthly'
      ? `${months[selectedMonth - 1]?.label || 'Month'} ${selectedYear}`
      : `Year ${selectedYear}`;
    return `${viewAsAdmin ? 'Revenue Report' : 'My Revenue Report'} - ${selectedDoctorLabel} - ${period}`;
  }, [months, reportType, selectedDoctorLabel, selectedMonth, selectedYear, viewAsAdmin]);

  const createExportNode = () => {
    const wrapper = document.createElement('div');
    wrapper.style.background = '#ffffff';
    wrapper.style.color = '#000000';
    wrapper.style.padding = '28px';
    wrapper.style.width = '1200px';
    wrapper.style.position = 'fixed';
    wrapper.style.left = '-20000px';
    wrapper.style.top = '0';
    wrapper.style.fontFamily = 'Arial, sans-serif';

    const periodLabel = reportType === 'monthly'
      ? `${months[selectedMonth - 1]?.label || 'Month'} ${selectedYear}`
      : `Year ${selectedYear}`;

    const reportCards = reports.map((report) => {
      const patientRows = (report.patients || []).map((patient) => {
        const transactionRows = (patient.transactions || []).map((transaction, index) => `
          <tr>
            <td style="padding:8px;border:1px solid #d1d5db;">${index + 1}</td>
            <td style="padding:8px;border:1px solid #d1d5db;">${formatDisplayDate(transaction.appointment_date || transaction.tracking_date)}</td>
            <td style="padding:8px;border:1px solid #d1d5db;">${transaction.appointment_id ? `#${transaction.appointment_id}` : 'N/A'}</td>
            <td style="padding:8px;border:1px solid #d1d5db;">#${transaction.tracking_id}</td>
            <td style="padding:8px;border:1px solid #d1d5db;text-align:right;">${formatCurrency(transaction.total_revenue)}</td>
            <td style="padding:8px;border:1px solid #d1d5db;text-align:right;">${formatCurrency(transaction.total_paid)}</td>
            <td style="padding:8px;border:1px solid #d1d5db;text-align:right;">${formatCurrency(transaction.total_pending)}</td>
          </tr>
        `).join('');

        return `
          <div style="margin:16px 0 22px 0;padding:14px;border:1px solid #d1d5db;border-radius:8px;background:#fafafa;">
            <div style="display:flex;justify-content:space-between;gap:16px;align-items:flex-start;margin-bottom:10px;">
              <div>
                <div style="font-size:16px;font-weight:700;color:#111827;">${patient.patient_name}</div>
                <div style="font-size:12px;color:#4b5563;margin-top:2px;">
                  Patient ID: ${patient.patient_unique_id || `ID: ${patient.patient_id}`} | Records: ${patient.tracking_records || 1}
                </div>
                <div style="font-size:12px;color:#4b5563;margin-top:2px;">
                  Mobile: ${patient.patient_phone || 'N/A'}
                </div>
              </div>
              <div style="text-align:right;font-size:12px;color:#111827;">
                <div><strong>Total:</strong> ${formatCurrency(patient.total_revenue)}</div>
                <div><strong>Paid:</strong> ${formatCurrency(patient.total_paid)}</div>
                <div><strong>Pending:</strong> ${formatCurrency(patient.total_pending)}</div>
              </div>
            </div>
            <table style="width:100%;border-collapse:collapse;font-size:12px;background:#ffffff;">
              <thead>
                <tr style="background:#f3f4f6;">
                  <th style="padding:8px;border:1px solid #d1d5db;text-align:left;">#</th>
                  <th style="padding:8px;border:1px solid #d1d5db;text-align:left;">Visit Date</th>
                  <th style="padding:8px;border:1px solid #d1d5db;text-align:left;">Appointment</th>
                  <th style="padding:8px;border:1px solid #d1d5db;text-align:left;">Record ID</th>
                  <th style="padding:8px;border:1px solid #d1d5db;text-align:right;">Charged</th>
                  <th style="padding:8px;border:1px solid #d1d5db;text-align:right;">Paid</th>
                  <th style="padding:8px;border:1px solid #d1d5db;text-align:right;">Pending</th>
                </tr>
              </thead>
              <tbody>
                ${transactionRows || `
                  <tr>
                    <td colspan="7" style="padding:10px;border:1px solid #d1d5db;color:#6b7280;text-align:center;">
                      No detailed visit transactions available
                    </td>
                  </tr>
                `}
              </tbody>
            </table>
          </div>
        `;
      }).join('');

      return `
        <section style="margin-top:24px;page-break-inside:avoid;">
          <div style="border:1px solid #d1d5db;border-radius:10px;overflow:hidden;">
            <div style="padding:16px 18px;border-bottom:1px solid #d1d5db;background:#f9fafb;">
              <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:16px;">
                <div>
                  <h2 style="margin:0;font-size:20px;color:#111827;">${report.doctor_name}</h2>
                  <p style="margin:4px 0 0 0;font-size:12px;color:#4b5563;">${report.patient_count} ${report.patient_count === 1 ? 'patient' : 'patients'}</p>
                </div>
                <div style="text-align:right;font-size:12px;color:#111827;">
                  <div><strong>Total Revenue:</strong> ${formatCurrency(report.total_revenue)}</div>
                  <div><strong>Total Paid:</strong> ${formatCurrency(report.total_paid)}</div>
                  <div><strong>Pending:</strong> ${formatCurrency(report.total_pending)}</div>
                </div>
              </div>
            </div>
            <div style="padding:18px;">
              ${patientRows || '<div style="font-size:13px;color:#6b7280;">No patient details available</div>'}
            </div>
          </div>
        </section>
      `;
    }).join('');

    wrapper.innerHTML = `
      <div style="border-bottom:2px solid #111827;padding-bottom:16px;margin-bottom:20px;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:24px;">
          <div style="display:flex;align-items:center;gap:14px;">
            <img
              src="${window.location.origin}/logo-print.png"
              alt="Gugan Dental Care Logo"
              style="width:64px;height:64px;object-fit:contain;"
              onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
            />
            <div style="display:none;width:64px;height:64px;border:1px solid #111827;border-radius:8px;align-items:center;justify-content:center;font-weight:700;font-size:18px;color:#111827;">
              GDC
            </div>
            <div>
              <h1 style="margin:0;font-size:28px;font-weight:700;color:#111827;">Gugan Dental Care</h1>
              <div style="margin-top:4px;font-size:12px;letter-spacing:0.08em;color:#4b5563;text-transform:uppercase;">Revenue Statement</div>
            </div>
          </div>
          <div style="text-align:right;font-size:12px;color:#111827;">
            <div><strong>Report:</strong> ${viewAsAdmin ? 'Revenue Report' : 'My Revenue Report'}</div>
            <div><strong>Doctor:</strong> ${selectedDoctorLabel}</div>
            <div><strong>Period:</strong> ${periodLabel}</div>
            <div><strong>Generated:</strong> ${new Date().toLocaleString('en-IN')}</div>
          </div>
        </div>
      </div>

      <div style="display:grid;grid-template-columns:repeat(3, 1fr);gap:16px;margin-bottom:22px;">
        <div style="border:1px solid #d1d5db;border-radius:8px;padding:14px;background:#ffffff;">
          <div style="font-size:12px;color:#6b7280;margin-bottom:6px;">${viewAsAdmin ? 'Total Revenue' : 'My Total Revenue'}</div>
          <div style="font-size:24px;font-weight:700;color:#111827;">${formatCurrency(overall.total_revenue)}</div>
        </div>
        <div style="border:1px solid #d1d5db;border-radius:8px;padding:14px;background:#ffffff;">
          <div style="font-size:12px;color:#6b7280;margin-bottom:6px;">${viewAsAdmin ? 'Total Paid' : 'Paid Amount'}</div>
          <div style="font-size:24px;font-weight:700;color:#111827;">${formatCurrency(overall.total_paid)}</div>
        </div>
        <div style="border:1px solid #d1d5db;border-radius:8px;padding:14px;background:#ffffff;">
          <div style="font-size:12px;color:#6b7280;margin-bottom:6px;">${viewAsAdmin ? 'Pending Payment' : 'Pending Amount'}</div>
          <div style="font-size:24px;font-weight:700;color:#111827;">${formatCurrency(overall.total_pending)}</div>
        </div>
      </div>

      ${reportCards || '<div style="font-size:14px;color:#6b7280;">No revenue data found for the selected period.</div>'}
    `;

    document.body.appendChild(wrapper);
    return wrapper;
  };

  const handlePrint = () => {
    const exportNode = createExportNode();
    if (!exportNode) return;

    const printWindow = window.open('', '_blank', 'width=1200,height=900');
    if (!printWindow) {
      toast({
        title: 'Error',
        description: 'Please allow pop-ups to print the report.',
        variant: 'destructive'
      });
      document.body.removeChild(exportNode);
      return;
    }

    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((node) => node.outerHTML)
      .join('\n');

    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>${exportTitle}</title>
          ${styles}
          <style>
            body { background: #ffffff; color: #000000; padding: 24px; font-family: Arial, sans-serif; }
            @page { size: A4 landscape; margin: 12mm; }
            table { width: 100%; border-collapse: collapse; }
            * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          </style>
        </head>
        <body>${exportNode.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
      document.body.removeChild(exportNode);
    }, 500);
  };

  // Fetch revenue reports
  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        report_type: reportType,
        year: selectedYear.toString()
      });

      if (reportType === 'monthly') {
        params.append('month', selectedMonth.toString());
      }

      if (isSelfRevenueView) {
        params.append('scope', 'self');
      } else if (selectedDoctor !== 'all') {
        params.append('doctor_id', selectedDoctor);
      }

      const data = await apiRequest(`get_revenue_reports.php?${params.toString()}`);
      
      if (data.success) {
        setReports(data.reports || []);
        setOverall(data.overall || {
          total_revenue: 0,
          total_paid: 0,
          total_pending: 0
        });
        setExpandedPatients({});
      } else {
        toast({
          title: 'Error',
          description: data.error || 'Failed to fetch revenue reports',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching revenue reports:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch revenue reports',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch reports when filters change
  useEffect(() => {
    fetchReports();
  }, [selectedMonth, selectedYear, selectedDoctor, reportType, viewAsAdmin, profile?.role]);

  // Filter doctors (only show doctors, chief_doctor, admin)
  const availableDoctors = doctors.filter(doc => 
    ['doctor', 'chief_doctor', 'admin'].includes(doc.role)
  );

  return (
    <div className="space-y-4">
      {/* Filters Card */}
      <Card className="glass-effect border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Revenue Reports
          </CardTitle>
          <CardDescription className="text-gray-400">
            View revenue breakdown by doctor, month, and year
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {/* Report Type */}
            <div>
              <Label className="text-white text-sm mb-2 block">Report Type</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Month (only for monthly reports) */}
            {reportType === 'monthly' && (
              <div>
                <Label className="text-white text-sm mb-2 block">Month</Label>
                <Select value={selectedMonth.toString()} onValueChange={(val) => setSelectedMonth(parseInt(val))}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(month => (
                      <SelectItem key={month.value} value={month.value.toString()}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Year */}
            <div>
              <Label className="text-white text-sm mb-2 block">Year</Label>
              <Select value={selectedYear.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Doctor Filter */}
            <div>
              <Label className="text-white text-sm mb-2 block">Doctor</Label>
              <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder="All Doctors" />
                </SelectTrigger>
                <SelectContent>
                  {!isSelfRevenueView ? (
                    <>
                      <SelectItem value="all">All Doctors</SelectItem>
                      {availableDoctors.map(doctor => (
                        <SelectItem key={doctor.id} value={doctor.id.toString()}>
                          {doctor.full_name || doctor.name || doctor.username}
                        </SelectItem>
                      ))}
                    </>
                  ) : (
                    <SelectItem value="all">My Revenue</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Refresh Button */}
            <div className="flex items-end">
              <Button
                onClick={fetchReports}
                disabled={loading}
                className="w-full medical-gradient text-white hover:opacity-90"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                onClick={handlePrint}
                variant="outline"
                className="w-full border-gray-600 bg-gray-800 text-white hover:bg-gray-700"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div ref={reportContentRef} className="space-y-4">
        {/* Overall Summary Card */}
        <Card className="glass-effect border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              {viewAsAdmin ? 'Overall Hospital Revenue' : 'My Revenue Report'}
            </CardTitle>
            <CardDescription className="text-gray-400">
              {reportType === 'monthly' 
                ? `${months[selectedMonth - 1].label} ${selectedYear}`
                : `Year ${selectedYear}`
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">{viewAsAdmin ? 'Total Revenue' : 'My Total Revenue'}</span>
                  <TrendingUp className="h-4 w-4 text-blue-400" />
                </div>
                <p className="text-2xl font-bold text-white">{formatCurrency(overall.total_revenue)}</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">{viewAsAdmin ? 'Total Paid' : 'Paid Amount'}</span>
                  <DollarSign className="h-4 w-4 text-green-400" />
                </div>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(overall.total_paid)}</p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm">{viewAsAdmin ? 'Pending Payment' : 'Pending Amount'}</span>
                  <AlertCircle className="h-4 w-4 text-red-400" />
                </div>
                <p className="text-2xl font-bold text-red-400">{formatCurrency(overall.total_pending)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Doctor-wise Breakdown */}
        {loading ? (
          <Card className="glass-effect border-gray-700">
            <CardContent className="p-6 text-center">
              <RefreshCw className="h-8 w-8 text-blue-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Loading revenue reports...</p>
            </CardContent>
          </Card>
        ) : reports.length === 0 ? (
          <Card className="glass-effect border-gray-700">
            <CardContent className="p-6 text-center">
              <BarChart3 className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No revenue data found for the selected period.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => (
            <Card key={report.doctor_id} className="glass-effect border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      {report.doctor_name}
                    </CardTitle>
                    <CardDescription className="text-gray-400 mt-1">
                      {report.patient_count} {report.patient_count === 1 ? 'patient' : 'patients'}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Doctor Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="text-gray-400 text-sm mb-1">Total Revenue</div>
                    <div className="text-xl font-bold text-white">{formatCurrency(report.total_revenue)}</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="text-gray-400 text-sm mb-1">Total Paid</div>
                    <div className="text-xl font-bold text-green-400">{formatCurrency(report.total_paid)}</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="text-gray-400 text-sm mb-1">Pending</div>
                    <div className="text-xl font-bold text-red-400">{formatCurrency(report.total_pending)}</div>
                  </div>
                </div>

                {/* Patient Details */}
                {report.patients && report.patients.length > 0 && (
                  <div>
                    <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Patient Details ({report.patients.length} {report.patients.length === 1 ? 'patient' : 'patients'})
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="border-b border-gray-700">
                            <th className="text-left text-gray-400 text-sm font-medium py-2 px-3">Patient Name</th>
                            <th className="text-left text-gray-400 text-sm font-medium py-2 px-3">Patient ID</th>
                            <th className="text-left text-gray-400 text-sm font-medium py-2 px-3">Mobile</th>
                            <th className="text-center text-gray-400 text-sm font-medium py-2 px-3">Records</th>
                            <th className="text-right text-gray-400 text-sm font-medium py-2 px-3">Total Revenue</th>
                            <th className="text-right text-gray-400 text-sm font-medium py-2 px-3">Paid</th>
                            <th className="text-right text-gray-400 text-sm font-medium py-2 px-3">Pending</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.patients.map((patient, idx) => {
                            const expandKey = `${report.doctor_id}-${patient.patient_id}`;
                            const isExpanded = !!expandedPatients[expandKey];
                            const transactionCount = (patient.transactions || []).length;
                            const hasTransactions = transactionCount > 0;
                            const canExpand = hasTransactions || (patient.tracking_records || 0) > 1;

                            return (
                              <React.Fragment key={`${patient.patient_id}-${idx}`}>
                                <tr className="border-b border-gray-800 hover:bg-gray-800/30 transition-colors">
                                  <td className="text-white py-2 px-3 font-medium">{patient.patient_name}</td>
                                  <td className="text-gray-400 py-2 px-3">{patient.patient_unique_id || `ID: ${patient.patient_id}`}</td>
                                  <td className="text-gray-300 py-2 px-3">{patient.patient_phone || 'N/A'}</td>
                                  <td className="text-gray-300 text-center py-2 px-3">
                                    <button
                                      type="button"
                                      onClick={() => togglePatientTransactions(report.doctor_id, patient.patient_id)}
                                      disabled={!canExpand}
                                      className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors ${
                                        canExpand
                                          ? 'bg-gray-700 hover:bg-gray-600 text-white'
                                          : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                                      }`}
                                    >
                                      {canExpand ? (
                                        isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />
                                      ) : null}
                                      <span>{patient.tracking_records || 1}</span>
                                    </button>
                                  </td>
                                  <td className="text-white text-right py-2 px-3 font-semibold">{formatCurrency(patient.total_revenue)}</td>
                                  <td className="text-green-400 text-right py-2 px-3">{formatCurrency(patient.total_paid)}</td>
                                  <td className={`text-right py-2 px-3 font-semibold ${patient.total_pending > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                    {formatCurrency(patient.total_pending)}
                                  </td>
                                </tr>
                                {isExpanded && (
                                  <tr className="border-b border-gray-800 bg-gray-900/40">
                                    <td colSpan={7} className="px-3 py-3">
                                      <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-3">
                                        <div className="mb-3 text-sm font-medium text-white">
                                          Visit-wise Transactions for {patient.patient_name}
                                        </div>
                                        {hasTransactions ? (
                                          <div className="overflow-x-auto">
                                            <table className="w-full border-collapse">
                                              <thead>
                                                <tr className="border-b border-gray-700">
                                                  <th className="text-left text-gray-400 text-xs font-medium py-2 px-2">Visit Date</th>
                                                  <th className="text-left text-gray-400 text-xs font-medium py-2 px-2">Appointment</th>
                                                  <th className="text-left text-gray-400 text-xs font-medium py-2 px-2">Record ID</th>
                                                  <th className="text-right text-gray-400 text-xs font-medium py-2 px-2">Charged</th>
                                                  <th className="text-right text-gray-400 text-xs font-medium py-2 px-2">Paid</th>
                                                  <th className="text-right text-gray-400 text-xs font-medium py-2 px-2">Pending</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {patient.transactions.map((transaction) => (
                                                  <tr key={transaction.tracking_id} className="border-b border-gray-800/80 last:border-b-0">
                                                    <td className="py-2 px-2 text-sm text-white">
                                                      {formatDisplayDate(transaction.appointment_date || transaction.tracking_date)}
                                                    </td>
                                                    <td className="py-2 px-2 text-sm text-gray-300">
                                                      {transaction.appointment_id ? `#${transaction.appointment_id}` : 'N/A'}
                                                    </td>
                                                    <td className="py-2 px-2 text-sm text-gray-300">
                                                      #{transaction.tracking_id}
                                                    </td>
                                                    <td className="py-2 px-2 text-sm text-right text-white">
                                                      {formatCurrency(transaction.total_revenue)}
                                                    </td>
                                                    <td className="py-2 px-2 text-sm text-right text-green-400">
                                                      {formatCurrency(transaction.total_paid)}
                                                    </td>
                                                    <td className={`py-2 px-2 text-sm text-right font-medium ${
                                                      transaction.total_pending > 0 ? 'text-red-400' : 'text-green-400'
                                                    }`}>
                                                      {formatCurrency(transaction.total_pending)}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        ) : (
                                          <div className="rounded-md border border-amber-700/50 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
                                            Detailed visit transactions are not available in the current API response for this patient yet.
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenueReports;
